import { LayoutRight } from '@untitledui/icons';
import { type MouseEvent, useCallback, useEffect, useState } from 'react';

import {
  AccordionChevron,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from '@/lib/base/accordion';
import { useSlideoutStackContextOptional } from '@/lib/crm/contexts/SlideoutStackContext';
import { EmailIframe } from '@/lib/email';
import type { TriggerEmailData } from '@/lib/proposals/core/types';

import { EmailProviderButton } from './CrmEventTriggerPreview/EmailThreadCard';
import { emailProviderFamily } from './emailThreadPanel.utils';
import {
  decodeHtmlEntities,
  extractSenderEmail,
  extractSenderName,
  formatRelativeTime,
  SenderAvatar,
  TimelineConnector,
  TriggerCardIconButton,
} from './shared';

/**
 * Email trigger card (existing email trigger type) with expand/collapse.
 *
 * Composes the shared `<Accordion>` / `<AccordionItem>` so this card
 * inherits the smooth grid-rows open/close animation, react-aria keyboard
 * handling (Enter / Space toggle, focus management), and consistent
 * design-system focus styling. The trigger row holds the email header
 * (sender · subject, then the trailing time / provider / thread / chevron
 * cluster shared with the CRM-event thread card) plus the snippet (visible
 * only when collapsed); the panel reveals the full email body (HTML iframe
 * or plain text) when expanded.
 */
function emailTriggerLabel({ isExpanded }: { isExpanded: boolean }) {
  return isExpanded ? 'Collapse email preview' : 'Expand email preview';
}

function renderEmailBody(email: TriggerEmailData['email']) {
  if (email.body_html) {
    return (
      <EmailIframe html={email.body_html} minHeight='60px' className='w-full' />
    );
  }
  return (
    <div className='text-xs text-secondary whitespace-pre-wrap'>
      {email.body}
    </div>
  );
}

export function EmailTriggerCard({
  trigger,
  onOpenThread,
  showConnector,
  defaultExpanded,
}: {
  trigger: TriggerEmailData;
  onOpenThread?: () => void;
  showConnector: boolean;
  defaultExpanded: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  // Lazy-mount the body so the email iframe (or large body div) only
  // renders once the user has expanded the card. Stays mounted after
  // first expand so collapse still animates from real content.
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    if (isExpanded && !hasMounted) {
      setHasMounted(true);
    }
  }, [isExpanded, hasMounted]);

  const handleViewThreadClick = useCallback(
    (e: MouseEvent) => {
      // endContent renders OUTSIDE the trigger button (sibling, not child)
      // so a normal click on this button no longer bubbles into the
      // disclosure toggle. We still stop propagation defensively in case
      // the surrounding card layout ever wraps both back into a single
      // click target.
      e.stopPropagation();
      onOpenThread?.();
    },
    [onOpenThread]
  );

  const { email } = trigger;
  const senderName = extractSenderName(email.from);
  const senderEmail = extractSenderEmail(email.from);
  const relativeTime = formatRelativeTime(email.date);

  // Highlight "View thread" while an email-thread slideout is open — same
  // open-state affordance as the header's record buttons. A kind-presence
  // check (not an exact entry-id match) is enough: any email_thread entry on
  // the stack while this proposal's card is on screen was opened from this
  // proposal, and a thread opened from a stacked record slideout covers this
  // card with its backdrop anyway.
  const slideoutStack = useSlideoutStackContextOptional();
  const isThreadOpen = !!slideoutStack?.stack.some(
    entry => entry.kind === 'email_thread'
  );

  return (
    <div className='mb-0'>
      <div className='w-full rounded-xl bg-bg-primary border border-secondary shadow-xs overflow-hidden'>
        {/* Standalone AccordionItem (no DisclosureGroup wrapper): a
            single controlled item honors `isExpanded` on mount, so
            `defaultExpanded` opens the card immediately. A DisclosureGroup
            would ignore the child's initial `isExpanded`. */}
        <AccordionItem isExpanded={isExpanded} onExpandedChange={setIsExpanded}>
          {/* Overlay trigger pattern: AccordionTrigger fills the row
                via `absolute inset-0`, so clicking anywhere — except on
                the trailing cluster's interactive buttons, which
                re-enable `pointer-events-auto` — toggles the
                disclosure. */}
          <div className='relative'>
            <AccordionTrigger
              aria-label={emailTriggerLabel({ isExpanded })}
              className='absolute inset-0 w-full focus-visible:ring-inset rounded'
            >
              {null}
            </AccordionTrigger>

            {/* Visible content layer. Header row sits above the
                  collapsed-state snippet, which spans the full card
                  width below — indented past the avatar so it aligns
                  with the header text and extends out to the card's
                  right edge. */}
            <div className='pointer-events-none relative'>
              <div className='flex items-center gap-3 px-4 pt-4'>
                <div className='flex-shrink-0'>
                  <SenderAvatar email={senderEmail} name={senderName} />
                </div>
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-1.5 min-w-0'>
                    <span className='text-sm font-medium text-fg-primary flex-shrink-0'>
                      {senderName}
                    </span>
                    <span className='text-tertiary flex-shrink-0'>·</span>
                    <span className='text-sm text-secondary truncate'>
                      {email.subject}
                    </span>
                  </div>
                </div>

                {/* Trailing meta cluster — time, provider, thread affordance,
                    chevron — mirrors CrmEventTriggerPreview's email-thread
                    row exactly, so the two trigger-card variants read the
                    same. Interactive children re-enable pointer-events so
                    they catch their own clicks instead of toggling the
                    disclosure overlay. */}
                <div className='flex items-center gap-2 text-xs text-tertiary flex-shrink-0'>
                  <span>{relativeTime}</span>
                  {/* Only a provider we can name gets the affordance —
                      EmailProviderButton reads any other provider as Gmail. */}
                  {trigger.external_link &&
                    emailProviderFamily(trigger.provider) !== null && (
                      <span className='pointer-events-auto'>
                        <EmailProviderButton
                          provider={trigger.provider}
                          externalLink={trigger.external_link}
                        />
                      </span>
                    )}
                  {onOpenThread && (
                    <TriggerCardIconButton
                      icon={LayoutRight}
                      label='View thread'
                      isPressed={isThreadOpen}
                      onClick={handleViewThreadClick}
                    />
                  )}
                  <AccordionChevron variant='default' className='w-4 h-4' />
                </div>
              </div>

              {/* Full-width collapsed-state snippet, indented past the
                    avatar (60px = 16px p-4 + 32px avatar + 12px gap) so
                    it aligns with the header text and extends to the
                    card's right edge — under where the trailing meta
                    sits in the header row above. */}
              {/* `truncate` clips to one line with the browser's
                    text-overflow ellipsis when the snippet overflows;
                    the trailing `…` keeps the affordance visible even
                    when the snippet happens to fit (the snippet is the
                    first ~140 chars of the email — there's almost
                    always more content in the expanded view).
                    Concatenated into a single text node so existing
                    test queries that match the raw snippet still find
                    it. */}
              {!isExpanded && (
                <div className='pl-[60px] pr-4 pb-4 pt-1 text-xs text-tertiary truncate'>
                  {`${decodeHtmlEntities(email.snippet)}…`}
                </div>
              )}
            </div>
          </div>

          <AccordionPanel className='px-4 pb-4'>
            {hasMounted && renderEmailBody(email)}
          </AccordionPanel>
        </AccordionItem>
        {/* Screen-reader live region — aria-expanded already announces
            state changes via the trigger button, but consumer tests rely
            on this human-readable phrasing too. */}
        <div className='sr-only' aria-live='polite' aria-atomic='true'>
          {isExpanded ? 'Email preview expanded' : 'Email preview collapsed'}
        </div>
      </div>
      {/* Connecting line to action below */}
      {showConnector && <TimelineConnector />}
    </div>
  );
}
